
import { distinct, filter, mergeMap } from 'rxjs/operators'
import * as moment from 'moment'
import { empty } from 'rxjs'
import { combineEpics } from 'redux-observable'
import { get } from 'helpers/observableAjaxHelpers'
import { normalize } from 'helpers/normalizeHelpers'
import { createTimeperiod } from 'helpers/dateTimeHelpers'
import { DATE_FORMAT_ISO_DAYLESS } from 'constants/datetime'
import { Epic } from 'interfaces/middleware'
import { TimeScale } from 'interfaces/ajax/base'
import { GET_MEMBER_CREDIT_TREND_URL, GET_MEMBER_NETWORTH_TREND_URL, GET_PEER_GROUP_CREDIT_TREND_URL, GET_PEER_GROUP_NETWORTH_TREND_URL } from 'interfaces/ajax'
import {
  appendPeergroupsGenericTrendsActionCreator,
  GET_MEMBER_GENERIC_TRENDS,
  GET_PEER_GROUP_GENERIC_TRENDS,
  setMemberGenericTrendsActionCreator,
} from 'actions/genericActions'
import { GenericTrendType } from 'interfaces/pages'
import { CREDIT_TREND_DISCARD_TRIGGERS, NETWORTH_PAGE_DISCARD_TRIGGERS, PEERGROUPS_DATA_DISCARD_TRIGGERS } from 'helpers/discardTriggerHelpers'
import { byType } from 'rxOperators/byType'
import { mapSafe } from 'rxOperators/mergeMapSafe'
import { toDiscardCache } from 'rxOperators/toDiscardCache'
import { accountsFilterQueryParamSelector } from 'components/features/pageFilters/selectors'

// ===========================================================================//
//                                MEMBER TRENDS                               //
// ===========================================================================//

const createGetMemberGenericTrendEpic = (type: GenericTrendType): Epic => {
  const discardTriggers = type === 'networth' ? NETWORTH_PAGE_DISCARD_TRIGGERS : CREDIT_TREND_DISCARD_TRIGGERS

  return (action$, store) =>
    action$
      .pipe(byType(GET_MEMBER_GENERIC_TRENDS)).pipe(
        filter(({ payload }) => payload.type === type),
        distinct(
          ({ payload }) => payload.timeframe,
          action$.pipe(toDiscardCache(discardTriggers)),
        ),
        mergeMap(({ payload: { timeframe }}) => {
          const state = store.getState()
          const { data } = state
          const loaded = Boolean(data[type].trends[timeframe])

          if (loaded) {
            return empty()
          }

          const timeperiod = createTimeperiod(timeframe)
          const scale: TimeScale = 'day'
          const requestPayload = {
            ...timeperiod,
            scale,
            excluded_account_uids: accountsFilterQueryParamSelector(state),
          }

          const request = type === 'creditscore' ?
            get(GET_MEMBER_CREDIT_TREND_URL, requestPayload) :
            get(GET_MEMBER_NETWORTH_TREND_URL, requestPayload)

          return request.pipe(mapSafe(({ response }) =>
            setMemberGenericTrendsActionCreator(type, timeframe, response)
          ))
        }))
}

// ===========================================================================//
//                              PEERGROUP TRENDS                              //
// ===========================================================================//

const createGetPeergroupsGenericTrendEpic = (type: GenericTrendType): Epic =>
  (action$, store) =>
    action$
      .pipe(byType(GET_PEER_GROUP_GENERIC_TRENDS)).pipe(
        filter(({ payload }) => payload.type === type),
        distinct(
          ({ payload }) => JSON.stringify(payload),
          action$.pipe(toDiscardCache(PEERGROUPS_DATA_DISCARD_TRIGGERS)),
        ),
        mergeMap(({ payload: { timeframe, peergroups }}) => {
          const state = store.getState()
          const useableTimeframe = timeframe ? timeframe : state.app[type].timeframe
          const loaded = state.data.peergroups[type].trends[useableTimeframe]

          const requestedGroups = loaded
            ? peergroups.filter((id) => !loaded.byKey[id])
            : peergroups

          const isCached = requestedGroups.length === 0
          if (isCached) {
            return empty()
          }

          const { start_date, end_date } = createTimeperiod(useableTimeframe)
          const requestPayload = {
            start_date: moment(start_date).subtract(1, 'month').format(DATE_FORMAT_ISO_DAYLESS),
            end_date,
            group: peergroups,
          }

          const request = type === 'creditscore'
            ? get(GET_PEER_GROUP_CREDIT_TREND_URL, requestPayload)
            : get(GET_PEER_GROUP_NETWORTH_TREND_URL, requestPayload)

          return request.pipe(mapSafe(({ response }) => {
            const trendsCollection = normalize(response, 'id')
            return appendPeergroupsGenericTrendsActionCreator(type, useableTimeframe, trendsCollection)
          }))
        }))

export default combineEpics(
  createGetMemberGenericTrendEpic('networth'),
  createGetMemberGenericTrendEpic('creditscore'),
  createGetPeergroupsGenericTrendEpic('networth'),
  createGetPeergroupsGenericTrendEpic('creditscore'),
)
